import { KatButton, KatModal } from '@amzn/katal-react';
import { FC, useEffect, useRef, useState } from 'react';
import styles from './ImageSelectionModal.module.css';

export interface ImageSelectionModalProps {
  isVisible: boolean;
  imageUrls: string[];
  onClose: () => void;
  onSelect: (selectedImageUrl: string) => void;
  showThumbnail?: boolean;
  title: string;
}

interface ImageDisplayProps {
  imageUrl: string;
  currentIndex: number;
  title: string;
  imageError: boolean;
  isLoading: boolean;
  onImageError: () => void;
  onImageLoad: () => void;
}

const ImageDisplay: FC<ImageDisplayProps> = ({
  imageUrl,
  currentIndex,
  title,
  imageError,
  isLoading,
  onImageError,
  onImageLoad,
}) => (
  <div className={styles.imageContainer}>
    {imageError && (
      <div className={styles.centeredText} role="alert">
        Failed to load image
      </div>
    )}
    {isLoading && !imageError && (
      <div className={styles.centeredText} aria-live="polite">
        Loading image...
      </div>
    )}
    <img
      src={imageUrl}
      alt={`Image ${currentIndex + 1} from ${title}`}
      className={`${styles.image} ${isLoading || imageError ? styles.imageHidden : ''}`}
      onError={onImageError}
      onLoad={onImageLoad}
    />
  </div>
);

interface ThumbnailCarouselProps {
  imageUrls: string[];
  currentIndex: number;
  title: string;
  onThumbnailClick: (index: number) => void;
  onSelect: () => void;
}

const ThumbnailCarousel: FC<ThumbnailCarouselProps> = ({
  imageUrls,
  currentIndex,
  title,
  onThumbnailClick,
  onSelect,
}) => {
  const thumbnailRefs = useRef<(HTMLImageElement | null)[]>([]);

  useEffect(() => {
    setTimeout(() => thumbnailRefs.current[0]?.focus(), 100);
  }, []);

  return (
    <div className={styles.thumbnailCarousel} role="tablist" aria-label="Image thumbnails">
      {imageUrls.map((url, index) => (
        <img
          key={index}
          ref={(el) => (thumbnailRefs.current[index] = el)}
          src={url}
          alt={`Thumbnail ${index + 1} from ${title}`}
          className={`${styles.thumbnail} ${index === currentIndex ? styles.activeThumbnail : ''}`}
          onClick={() => onThumbnailClick(index)}
          role="tab"
          aria-selected={index === currentIndex}
          aria-label={`Select image ${index + 1}`}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onThumbnailClick(index);
              onSelect();
            } else if (e.key === 'ArrowLeft') {
              e.preventDefault();
              const prevIndex = index === 0 ? imageUrls.length - 1 : index - 1;
              onThumbnailClick(prevIndex);
              thumbnailRefs.current[prevIndex]?.focus();
            } else if (e.key === 'ArrowRight') {
              e.preventDefault();
              const nextIndex = index === imageUrls.length - 1 ? 0 : index + 1;
              onThumbnailClick(nextIndex);
              thumbnailRefs.current[nextIndex]?.focus();
            }
          }}
        />
      ))}
    </div>
  );
};

interface NavigationControlsProps {
  currentIndex: number;
  totalImages: number;
  onPrevious: () => void;
  onNext: () => void;
}

const NavigationControls: FC<NavigationControlsProps> = ({
  currentIndex,
  totalImages,
  onPrevious,
  onNext,
}) => (
  <div className={styles.navigationButtons} role="group" aria-label="Image navigation">
    <KatButton
      variant="secondary"
      label="Previous"
      onClick={onPrevious}
      disabled={totalImages <= 1}
      data-testid="previous-button"
      className={styles.navigationButton}
      aria-label={`Go to previous image (${currentIndex === 0 ? totalImages : currentIndex} of ${totalImages})`}
    />
    <span className={styles.counter} aria-live="polite">
      {currentIndex + 1} of {totalImages}
    </span>
    <KatButton
      variant="secondary"
      label="Next"
      onClick={onNext}
      disabled={totalImages <= 1}
      data-testid="next-button"
      className={styles.navigationButton}
      aria-label={`Go to next image (${currentIndex === totalImages - 1 ? 1 : currentIndex + 2} of ${totalImages})`}
    />
  </div>
);

export const ImageSelectionModal: FC<ImageSelectionModalProps> = ({
  isVisible,
  imageUrls,
  onClose,
  onSelect,
  showThumbnail = true,
  title,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isVisible) {
      setCurrentIndex(0);
      setImageError(false);
      setIsLoading(true);
    }
  }, [isVisible, imageUrls]);

  useEffect(() => {
    setImageError(false);
    setIsLoading(true);
  }, [currentIndex]);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? imageUrls.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === imageUrls.length - 1 ? 0 : prev + 1));
  };

  const handleSelect = () => {
    onSelect(imageUrls[currentIndex]);
  };

  if (!imageUrls.length || !isVisible) return null;

  return (
    <KatModal
      visible={isVisible}
      onClose={onClose}
      data-testid="image-selection-modal"
      className={styles.modal}
      aria-label={`Image selection modal for ${title}`}
    >
      <span slot="title">{`Pick an image from ${title}`}</span>
      <div className={styles.modalContent}>
        <ImageDisplay
          imageUrl={imageUrls[currentIndex]}
          currentIndex={currentIndex}
          title={title}
          imageError={imageError}
          isLoading={isLoading}
          onImageError={() => setImageError(true)}
          onImageLoad={() => {
            setIsLoading(false);
            setImageError(false);
          }}
        />
        {showThumbnail && (
          <ThumbnailCarousel
            imageUrls={imageUrls}
            currentIndex={currentIndex}
            title={title}
            onThumbnailClick={setCurrentIndex}
            onSelect={handleSelect}
          />
        )}
        <NavigationControls
          currentIndex={currentIndex}
          totalImages={imageUrls.length}
          onPrevious={handlePrevious}
          onNext={handleNext}
        />
      </div>
      <div slot="footer" className={styles.footer}>
        <KatButton
          variant="primary"
          label="Select"
          onClick={handleSelect}
          className={styles.selectButton}
          data-testid="select-button"
          aria-label={`Select current image (${currentIndex + 1} of ${imageUrls.length})`}
        />
      </div>
    </KatModal>
  );
};
